/**
 * Provider Registry
 * Manages available AI providers — models come from the Worker API.
 * No client-side API keys needed.
 */

import { fetchModels } from '../lib/api.js';

class ProviderRegistry {
  constructor() {
    this.providers = new Map();
    this.loaded = false;
    this.initDefaults();
  }

  initDefaults() {
    // Defaults shown immediately — real availability checked via Worker /api/models
    this.register('google', {
      id: 'google',
      name: 'Google Antigravity',
      model: 'claude-opus-4-6-thinking',
      configured: true,
      models: [
        { id: 'claude-opus-4-6-thinking', name: 'Claude Opus 4.6 (Thinking)' },
        { id: 'claude-sonnet-4-5-thinking', name: 'Claude Sonnet 4.5 (Thinking)' },
        { id: 'claude-sonnet-4-5', name: 'Claude Sonnet 4.5' },
        { id: 'gemini-3-pro', name: 'Gemini 3 Pro' },
        { id: 'gemini-3-flash', name: 'Gemini 3 Flash' },
        { id: 'gemini-2.5-pro', name: 'Gemini 2.5 Pro' },
        { id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash' },
      ],
    });

    this.register('deepseek', {
      id: 'deepseek',
      name: 'DeepSeek',
      model: 'deepseek-chat',
      configured: false,
      models: [
        { id: 'deepseek-chat', name: 'DeepSeek Chat' },
        { id: 'deepseek-reasoner', name: 'DeepSeek Reasoner' },
      ],
    });

    this.register('minimax', {
      id: 'minimax',
      name: 'MiniMax',
      model: 'MiniMax-M2.1',
      configured: false,
      models: [
        { id: 'MiniMax-M2.1', name: 'MiniMax M2.1' },
        { id: 'MiniMax-Text-01', name: 'MiniMax Text 01' },
      ],
    });

    this.register('kimi', {
      id: 'kimi',
      name: 'Kimi (Moonshot)',
      model: 'kimi-k2-0711',
      configured: false,
      models: [
        { id: 'kimi-k2-0711', name: 'Kimi K2' },
        { id: 'moonshot-v1-auto', name: 'Moonshot V1 Auto' },
        { id: 'moonshot-v1-128k', name: 'Moonshot V1 128K' },
      ],
    });

    this.register('zai', {
      id: 'zai',
      name: 'Z.AI',
      model: 'glm-4.7',
      configured: false,
      models: [
        { id: 'glm-4.7', name: 'GLM 4.7' },
        { id: 'glm-4.7-flash', name: 'GLM 4.7 Flash' },
        { id: 'glm-4.6', name: 'GLM 4.6' },
      ],
    });

    this.register('openclaw', {
      id: 'openclaw',
      name: 'OpenClaw',
      model: 'vps:main',
      configured: false,
      models: [], // Populated dynamically from worker API
    });
  }

  async loadFromWorker() {
    try {
      const data = await fetchModels();
      if (data.providers && data.providers.length > 0) {
        // Mark all as unconfigured first, then mark what's available
        for (const [, p] of this.providers) {
          p.configured = false;
        }

        for (const p of data.providers) {
          const existing = this.providers.get(p.id);
          if (existing) {
            existing.configured = true;
            if (p.models) existing.models = p.models;
          } else {
            this.register(p.id, {
              id: p.id,
              name: p.name,
              model: p.models?.[0]?.id || '',
              configured: true,
              models: p.models || [],
            });
          }
        }
        this.loaded = true;
      }
    } catch (error) {
      console.warn('Could not load models from Worker:', error);
    }
  }

  register(id, provider) {
    this.providers.set(id, provider);
  }

  get(id) {
    return this.providers.get(id);
  }

  getAll() {
    return [...this.providers.values()];
  }

  getConfigured() {
    return [...this.providers.values()].filter(p => p.configured);
  }

  setModel(providerId, modelId) {
    const provider = this.providers.get(providerId);
    if (provider) {
      provider.model = modelId;
      this.saveSelection();
    }
  }

  getActive() {
    const saved = this.loadSelection();
    if (saved && this.providers.get(saved.providerId)?.configured) {
      return saved;
    }
    const configured = this.getConfigured();
    if (configured.length > 0) {
      return { providerId: configured[0].id, model: configured[0].model };
    }
    return null;
  }

  setActive(providerId, model) {
    const provider = this.providers.get(providerId);
    if (provider) {
      provider.model = model || provider.model;
      this.saveSelection({ providerId, model: provider.model });
    }
  }

  saveSelection(selection) {
    try {
      localStorage.setItem('chorus_active_provider', JSON.stringify(selection || this.getActive()));
    } catch {}
  }

  loadSelection() {
    try {
      const saved = localStorage.getItem('chorus_active_provider');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  }
}

export const providers = new ProviderRegistry();
